import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { ProductDraft } from './ProductDialog';
import { getPublicAssetUrl } from '../../utils/storageConfig';
import { detectVendorPreset, getComboKey } from '../vendorPresets';

interface BulkImportDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (products: ProductDraft[]) => Promise<void>;
}

interface ParsedProduct {
  row: number;
  data: ProductDraft;
  errors: string[];
}

const normalizeDomainInput = (value: string): string => {
  const normalized = value.toLowerCase().trim().replace(/\s+/g, '-');
  if (normalized === 'mobile' || normalized === 'mobile-hardware' || normalized === 'telstra-handset' || normalized === 'telstra-handsets') {
    return 'mobile-hardware';
  }
  return normalized;
};

export function BulkImportDialog({ open, onClose, onConfirm }: BulkImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedProducts, setParsedProducts] = useState<ParsedProduct[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setParseError(null);
      setParsedProducts([]);
    }
  }, []);

  const parseFile = useCallback(async () => {
    if (!file) return;
    
    setIsProcessing(true);
    setParseError(null);
    
    try {
      let rows: any[] = [];
      const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

      if (isExcel) {
        // Parse Excel file
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      } else {
        // Parse CSV file
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());
        rows = lines.map(line => parseCSVLine(line));
      }

      if (rows.length < 2) {
        setParseError('File must contain a header row and at least one data row');
        setIsProcessing(false);
        return;
      }

      // Parse header
      const headers = rows[0].map((h: any) => String(h || '').trim().toLowerCase());
      
      // Parse rows
      const products: ParsedProduct[] = [];
      
      for (let i = 1; i < rows.length; i++) {
        const values = rows[i];
        if (!values || values.length === 0 || !values.some((v: any) => v)) continue; // Skip empty rows
        
        const errors: string[] = [];
        
        // Map columns to product fields
        const getField = (...fieldNames: string[]) => {
          for (const field of fieldNames) {
            const idx = headers.indexOf(field.toLowerCase());
            if (idx >= 0 && values[idx]) {
              return String(values[idx]).trim();
            }
          }
          return '';
        };

        const name = getField('name', 'product name', 'product');
        const rawDomain = getField('domain', 'category', 'placement').toLowerCase();
        const domain = normalizeDomainInput(rawDomain);
        const vendorOrBrand = getField('vendor', 'brand', 'manufacturer');
        const description = getField('description', 'desc');
        const priceStr = getField('price', 'cost');
        const pricingType = getField('pricing type', 'pricingtype', 'pricing').toLowerCase();
        const termStr = getField('term', 'term (months)', 'contract term');
        const billingCycle = getField('billing cycle', 'billingcycle', 'billing').toLowerCase();
        const image = getField('image', 'image url', 'imageurl', 'logo');
        const colour = getField('colour', 'color');
        const capacity = getField('capacity', 'storage');

        // Validation
        if (!name) errors.push('Name is required');
        if (!vendorOrBrand) errors.push('Vendor/Brand is required');
        
        const price = parseFloat(priceStr) || 0;
        if (price < 0) errors.push('Price must be >= 0');

        // Validate and map domain first (needed for pricing type logic)
        const validDomains = ['software', 'computers', 'networking', 'mobile-hardware', 'consulting', 'telstra', 'unified-collaboration'];
        const visibleDomains = ['software', 'computers', 'networking', 'consulting', 'telstra', 'telstra-handsets', 'unified-collaboration'];
        let finalDomain: ProductDraft['domain'] = 'software';
        
        if (!rawDomain) {
          errors.push('Domain is required');
          finalDomain = 'software';
        } else if (validDomains.includes(domain)) {
          finalDomain = domain as ProductDraft['domain'];
        } else {
          errors.push(`Invalid domain: "${rawDomain}". Must be one of: ${visibleDomains.join(', ')}`);
          finalDomain = 'software';
        }

        // Determine pricing type
        let finalPricingType: 'perUser' | 'lumpSum' = 'perUser';
        if (pricingType === 'lump sum' || pricingType === 'lumpsum' || pricingType === 'upfront') {
          finalPricingType = 'lumpSum';
        } else if (pricingType === 'per user' || pricingType === 'peruser' || pricingType === 'subscription') {
          finalPricingType = 'perUser';
        } else if (finalDomain === 'software') {
          // Default for software: per user
          finalPricingType = 'perUser';
        } else {
          // Default for hardware/consulting: lump sum
          finalPricingType = 'lumpSum';
        }

        // Map kind
        let finalKind: ProductDraft['kind'] = 'software';
        if (finalDomain === 'software') {
          finalKind = 'software';
        } else if (finalDomain === 'consulting') {
          finalKind = 'consulting';
        } else {
          finalKind = 'hardware';
        }

        const product: ProductDraft = {
          kind: finalKind,
          domain: finalDomain,
          name,
          vendorOrBrand,
          description,
          price,
          pricingType: finalPricingType,
          image: image || undefined,
          colour: colour || undefined,
          capacity: capacity || undefined,
        };

        // Detect vendor preset for term validation
        const vendorPresetMatch = detectVendorPreset(vendorOrBrand);
        if (vendorPresetMatch) {
          product.vendorPreset = vendorPresetMatch.id;
          product.vendorOrBrand = vendorPresetMatch.label;
        }

        if (finalPricingType !== 'lumpSum') {
          const parsedTerm = parseInt(termStr);
          let term = isNaN(parsedTerm) ? (vendorPresetMatch?.defaultTerm ?? 12) : parsedTerm;

          if (vendorPresetMatch) {
            const validTerms = Array.from(new Set(vendorPresetMatch.validCombinations.map(c => c.term)));
            if (!validTerms.includes(term)) {
              errors.push(`${vendorPresetMatch.label} only supports terms: ${validTerms.join(', ')} months — corrected to ${vendorPresetMatch.defaultTerm}`);
              term = vendorPresetMatch.defaultTerm;
            }
          } else if (![0, 6, 12, 18, 24, 36].includes(term)) {
            errors.push('Term must be Casual (0), 6, 12, 18, 24, or 36 months');
          }
          product.term = term;

          const cycle = billingCycle === 'annual' || billingCycle === 'yearly' ? 'annual' : 'monthly';
          product.billingCycle = cycle;
        }

        if (vendorPresetMatch) {
          const tp: Record<string, number> = {};
          for (const combo of vendorPresetMatch.validCombinations) {
            tp[getComboKey(combo.billingCycle, combo.term)] = price;
          }
          product.termPricing = tp;
        }

        products.push({
          row: i + 1,
          data: product,
          errors
        });
      }

      setParsedProducts(products);
    } catch (e: any) {
      setParseError(e.message || 'Failed to parse file');
    } finally {
      setIsProcessing(false);
    }
  }, [file]);

  const handleConfirm = useCallback(async () => {
    const validProducts = parsedProducts.filter(p => p.errors.length === 0).map(p => p.data);
    if (validProducts.length === 0) return;
    
    setIsProcessing(true);
    try {
      await onConfirm(validProducts);
      onClose();
    } catch (e) {
      setParseError('Failed to import products. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [parsedProducts, onConfirm, onClose]);

  const handleCancel = useCallback(() => {
    setFile(null);
    setParsedProducts([]);
    setParseError(null);
    onClose();
  }, [onClose]);

  const downloadTemplate = useCallback(async () => {
    try {
      // Template URL uses runtime environment detection for storage account
      // SAS token expires 2026-09-22, read-only container-level access
      
      // Add aggressive cache-busting with timestamp and random string
      const cacheBuster = `cb=${Date.now()}_${Math.random().toString(36).substring(7)}`;
      const templateUrl = getPublicAssetUrl('templates/aitryx-product-bulk-import.xlsx', cacheBuster);
      
      // Try to download from blob storage first (works with or without SAS token)
      try {
        const response = await fetch(templateUrl, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'aitryx-product-bulk-import.xlsx';
          a.click();
          URL.revokeObjectURL(url);
          return;
        }
      } catch (e) {
        // Fall back to client-side template generation
      }

      // Fallback: Generate template client-side
      const wb = XLSX.utils.book_new();
      
      const headers = [
        'Name',
        'Vendor',
        'Domain',
        'Price',
        'Pricing Type',
        'Term (months)',
        'Billing Cycle',
        'Description',
        'Image URL',
        'Colour',
        'Capacity'
      ];
      
      const exampleRows = [
        [
          'Microsoft 365 Business',
          'Microsoft',
          'software',
          12.50,
          'per user',
          12,
          'monthly',
          'Office productivity suite',
          'https://example.com/m365.png',
          '',
          ''
        ],
        [
          'Dell Latitude 5430',
          'Dell',
          'computers',
          1499.99,
          'lump sum',
          '',
          '',
          '14" business laptop',
          'https://example.com/latitude.png',
          'Black',
          '512GB SSD, 16GB RAM'
        ]
      ];

      const wsData = [headers, ...exampleRows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      ws['!cols'] = [
        { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
        { wch: 12 }, { wch: 12 }, { wch: 30 }, { wch: 35 }, { wch: 12 }, { wch: 20 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Products');

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aitryx-product-bulk-import.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to download template. Please try again.');
    }
  }, []);

  const validCount = parsedProducts.filter(p => p.errors.length === 0).length;
  const errorCount = parsedProducts.filter(p => p.errors.length > 0).length;

  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={handleCancel} />
      <div className="relative z-10 w-full max-w-5xl rounded-2xl bg-white shadow-xl border border-gray-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Bulk Import Products</h2>
            <p className="text-sm text-gray-500 mt-1">Import products from CSV or Excel file with dropdown validation</p>
          </div>
          <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close dialog">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          {/* Instructions Section */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">📋 How to Use</h3>
            <div className="text-sm text-blue-800 space-y-2">
              <p><strong>Step 1:</strong> Download the Excel template below</p>
              <p><strong>Step 2:</strong> Fill in your product data using the dropdown lists provided</p>
              <p><strong>Step 3:</strong> Upload the completed file and click "Parse File" to preview</p>
              <p><strong>Step 4:</strong> Review for errors, then click "Import" to add products</p>
            </div>
            
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs font-semibold text-blue-900 mb-1.5">Required Fields:</p>
              <ul className="text-xs text-blue-800 space-y-0.5 ml-4">
                <li>• <strong>Name</strong> - Product name</li>
                <li>• <strong>Vendor</strong> - Vendor/Brand/Manufacturer</li>
                <li>• <strong>Domain</strong> - Use dropdown (software, computers, networking, consulting, telstra, telstra-handsets)</li>
                <li>• <strong>Price</strong> - Base price (number ≥ 0)</li>
                <li>• <strong>Pricing Type</strong> - Use dropdown ("per user" or "lump sum")</li>
                <li>• <strong>Term</strong> - Required ONLY if "per user" - Use dropdown (Casual, 6, 12, 18, 24, 36 months)</li>
                <li>• <strong>Billing Cycle</strong> - Required ONLY if "per user" - Use dropdown ("monthly" or "annual")</li>
              </ul>
              
              <p className="text-xs font-semibold text-blue-900 mb-1.5 mt-2">Optional Fields:</p>
              <ul className="text-xs text-blue-800 space-y-0.5 ml-4">
                <li>• <strong>Description</strong> - Product details</li>
                <li>• <strong>Image URL</strong> - Complete URL to product image</li>
                <li>• <strong>Colour</strong> - Product color (e.g., "Black", "Silver")</li>
                <li>• <strong>Capacity</strong> - Storage/RAM specs (e.g., "512GB SSD, 16GB RAM")</li>
              </ul>
              
              <p className="text-xs text-blue-700 mt-2 italic">💡 Tip: Use Excel dropdowns to avoid typos and errors!</p>
            </div>
          </div>

          {/* Upload Section */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <label className="block">
                <span className="sr-only">Choose file</span>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="block text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                />
              </label>
              <button
                onClick={downloadTemplate}
                className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 4v16m0 0l-4-4m4 4l4-4" />
                </svg>
                Download Template
              </button>
            </div>
            
            {file && !parsedProducts.length && (
              <button
                onClick={parseFile}
                disabled={isProcessing}
                className="inline-flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
              >
                {isProcessing ? 'Parsing...' : 'Parse File'}
              </button>
            )}

            {parseError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                {parseError}
              </div>
            )}
          </div>

          {/* Preview Section */}
          {parsedProducts.length > 0 && (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">
                  Preview ({validCount} valid, {errorCount} with errors)
                </h3>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Row</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Vendor/Brand</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Price</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Pricing</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parsedProducts.map((p, idx) => (
                        <tr key={idx} className={p.errors.length > 0 ? 'bg-red-50' : ''}>
                          <td className="px-3 py-2 text-sm text-gray-900">{p.row}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{p.data.name}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">{p.data.domain}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">{p.data.vendorOrBrand}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">${(p.data.price ?? 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {p.data.pricingType === 'perUser' ? 'Per User' : 'Lump Sum'}
                            {p.data.term && p.data.billingCycle && (
                              <span className="text-xs text-gray-500 ml-1">
                                ({p.data.term}mo, {p.data.billingCycle})
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {p.errors.length > 0 ? (
                              <div className="text-red-600 text-xs">
                                {p.errors.map((err, i) => (
                                  <div key={i}>• {err}</div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-green-600 text-xs">✓ Valid</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            {validCount > 0 && (
              <span>{validCount} product{validCount !== 1 ? 's' : ''} ready to import</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={validCount === 0 || isProcessing}
              className={`px-4 py-2 text-sm rounded-md ${
                validCount > 0 && !isProcessing
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isProcessing ? 'Importing...' : `Import ${validCount} Product${validCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to parse CSV line (handles quoted fields with commas)
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current);
  return result;
}

export default BulkImportDialog;
